/* eslint-disable import/no-cycle */
/**
 * @overview Search page filter behaviour.
 *
 * @module search/filtering
 */

import $ from 'qwery';
import serialize from 'form-serialize';
import { fetchJSON } from '../../helpers/fetchHelper';
import { dispatchEvent, dispatchCustomEvent } from '../../helpers/eventHelper';
import { uncheckEveryCheckbox, checkInputsByValue } from '../../helpers/checkboxHelper';
import { pushHistory, removeQueryStringKeys } from '../../helpers/urlHelper';
import { showLoader, hideLoader } from '../../modules/loader';
import { lazyImages } from '../../modules/lazyImages';
import { hideSortPopover, updateSortOrders } from './sortOrders';
import { getSearchArea } from './helpers';
import { updateCuisines } from './cuisines';
import { updateRefinements } from './refinements';
import { updateDeliveryTypeFilter } from './deliveryTypeFilter';
import { pushUpdatesToDataLayer } from './analytics';
import AttributeBinding from '../../modules/AttributeBinding';


/**
 * Takes the state of the filters form and pushes that state into the history.
 *
 * @param {HTMLFormElement} form
 */
function pushHistoryState (serialized, hashed) {
    const rawQueryString = window.location.search;
    const removeKeys = ['cuisine', 'refine', 'so', 'lat', 'long', 'deliveryType'];
    const additionalQsValues = removeQueryStringKeys(rawQueryString, removeKeys);

    // take serialized items and combine with any existing querystring parameters
    const queryString = [serialized, additionalQsValues]
        .filter(qs => qs.length > 0)
        .join('&');

    pushHistory({ hashedForm: hashed }, `?${queryString}`);
}

/**
 * Updates the restaurant count description text.
 *
 * @param {string} countDescription
 */
function updateRestaurantCount (countDescription) {
    $('[data-filtered-restaurant-count]').forEach(span => {
        span.innerHTML = `(${countDescription})`;
    });
}

/**
 * Updates content on page following a filter event.
 *
 * @param {object} response
 */
function updateContent (response) {
    const searchListings = $('[data-search-listings]')[0];
    const pageHeading = $('[data-search-heading]')[0];
    const sortTitles = $('[data-sort-order-title-selected]');

    if (searchListings) {
        searchListings.innerHTML = response.template;
        AttributeBinding.applyBindings(searchListings);
    }

    if (pageHeading) {
        pageHeading.innerHTML = response.heading;
    }

    if (sortTitles && sortTitles.length > 0) {
        sortTitles.forEach(title => {
            title.innerHTML = response.sortOrderHeading;
        });
    }

    lazyImages();
    updateCuisines(response.cuisines);
    updateRefinements(response.disabledFilters);
    updateDeliveryTypeFilter(response.updatedDeliveryTypeFilterItems);
    updateSortOrders();
    updateRestaurantCount(response.restaurantCountText);
}


/**
 * Contains the timeout id of the filterRestaurants fetch call - used for debouncing.
 * @type {number}
 * @see filterRestaurants
 */
let filterTimeout;

/**
 * Filters restaurant results based on selected filters.
 *
 * @param {boolean} [addToHistory=true] Add a record to the browser history?
 * @param {boolean} [scrollToTop=false] Scroll the user to the top of page?
 */
export function filterRestaurants ({ addToHistory = true, scrollToTop = false } = {}) {
    const debounceDelay = 1000;
    const showLoaderOnElements = $('body');

    showLoader(showLoaderOnElements, { isPositionFixed: true, isOpaque: true });

    // Debounce the filter in order to reduce the number of network requests
    if (filterTimeout) {
        clearTimeout(filterTimeout);
    }

    filterTimeout = setTimeout(() => {

        const filtersForm = document.querySelector('[data-filters-form]');
        var orderTypeFilters = document.querySelector('[data-ordertype-filters-form]');
        const orderTypeFormSerialized = `${serialize(orderTypeFilters)}`;
        var hashed = serialize(filtersForm, { hash: true });
        var serialized = serialize(filtersForm);
        if (orderTypeFormSerialized) {
            serialized += `&${orderTypeFormSerialized}`;
            hashed += `&${serialize(orderTypeFilters, { hash: true })}`;
        }
        const formUrl = filtersForm.getAttribute('action');
        const area = getSearchArea(formUrl);

        fetchJSON(`/area/filter-restaurants/${area}?${serialized}`)
            .then(response => {
                updateContent(response);

                dispatchCustomEvent('serpFiltered', { filtered: true, scrollToTop });

                if (addToHistory) {
                    pushHistoryState(serialized, hashed);
                }

                pushUpdatesToDataLayer(response.serpData);
            })
            .catch(() => {
                dispatchCustomEvent('serpFiltered', { filtered: false, scrollToTop });
            })
            // This final `then()` acts like a `finally()` - https://gist.github.com/jish/e9bcd75e391a2b21206b
            .then(() => {
                hideSortPopover();
                hideLoader(showLoaderOnElements);
            });

    }, debounceDelay);
}

/**
 * Set selected filters
 *
 * @param {string[]} filters
 * @param {string|Node} context
 */
function setSelectedFilters (filters, context) {
    // Reset the checked values of all checkboxes in context
    uncheckEveryCheckbox(context);
    // Check inputs with values which match values in filters
    checkInputsByValue(filters || '', context);
}

/**
 * Update active item class for filters.
 *
 * @param {Node[]} items
 */
export function setActiveItems (items) {
    items.forEach(item => {
        const input = item.querySelector('input');

        if (input.checked) {
            item.classList.add('is-active');
        } else {
            item.classList.remove('is-active');
        }
    });
}

/**
 * Event which fires when a session history entry is being navigated to.
 *
 * @param {Event} event
 */
function onPopState (event) {
    const defaultHash = { cuisine: 'all', so: '' };
    const {
        cuisine, refine, deliveryType, so
    } = (event.state && event.state.hashedForm) || defaultHash;

    setSelectedFilters(cuisine, '[data-cuisine-filter]');
    setSelectedFilters(refine, '[data-refinements]');
    setSelectedFilters(deliveryType, '[data-delivery-type-filter]');
    setSelectedFilters(so, '[data-sort-order]');

    filterRestaurants({ addToHistory: false, scrollToTop: false });

    dispatchEvent('MegaModal:close');
}


(function initFilters () {
    if (document.querySelector('[data-filters-form]')) {
        window.addEventListener('popstate', onPopState);
    }
}());
